import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { AlarmSoundId, DEFAULT_ALARM_SOUND, isAlarmSound } from './alarmSounds';
import { CompletionId, DEFAULT_LOOK, Look } from './catalog';
import { canRecover, missedWeekdays } from './streak';
import { DEFAULT_DURATIONS, Durations } from './timer';

export interface Stats {
    /** yyyy-mm-dd of the last finished focus session. */
    lastDay: string;
    today: number;
    total: number;
    streak: number;
    /** Day of the last recovered streak ('' = never). Limits how often one can be won back. */
    lastRecovery: string;
    /** A streak that just restarted but may be offered back, for the day it was lost. */
    lostStreak: { streak: number; day: string } | null;
}

export interface TomatoState {
    look: Look;
    completion: CompletionId;
    muted: boolean;
    /** The end-of-timer sound, for every kind of timer. */
    alarmSound: AlarmSoundId;
    /** Phone: a full-screen alarm over the lock screen when a timer ends. */
    lockScreenAlarm: boolean;
    /** Phone: notifications that cheer the streak on. */
    streakReminders: boolean;
    durations: Durations;
    stats: Stats;
    /** Cached from the store; the store is the source of truth on a phone. */
    lifetime: boolean;
}

const STORAGE_KEY = 'tomato-pop:v1';

export const dayKey = (d: Date): string =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export const defaultState = (): TomatoState => ({
    look: DEFAULT_LOOK,
    completion: 'explode',
    muted: false,
    alarmSound: DEFAULT_ALARM_SOUND,
    lockScreenAlarm: true,
    streakReminders: true,
    durations: DEFAULT_DURATIONS,
    stats: {
        lastDay: '',
        today: 0,
        total: 0,
        streak: 0,
        lastRecovery: '',
        lostStreak: null,
    },
    lifetime: false,
});

/** Saved looks from before an option was renamed. */
const migrateLook = (look: Look): Look =>
    (look.neck as string) === 'tie' ? { ...look, neck: 'tshirt' } : look;

export const loadTomatoState = (): TomatoState => {
    const base = defaultState();
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return base;
        const saved = JSON.parse(raw) as Partial<TomatoState>;
        return {
            ...base,
            ...saved,
            alarmSound: isAlarmSound(saved.alarmSound)
                ? saved.alarmSound
                : base.alarmSound,
            look: migrateLook({ ...base.look, ...saved.look }),
            durations: { ...base.durations, ...saved.durations },
            stats: { ...base.stats, ...saved.stats },
        };
    } catch {
        return base;
    }
};

export const saveTomatoState = (state: TomatoState): void => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
        // Private mode or full storage: the settings just do not persist.
    }
};

const clampMinutes = (n: number) =>
    Math.min(180, Math.max(1, Math.round(n) || 1));

const tomatoSlice = createSlice({
    name: 'tomato',
    initialState: loadTomatoState,
    reducers: {
        setLook(state, action: PayloadAction<Partial<Look>>) {
            state.look = { ...state.look, ...action.payload };
        },
        setCompletion(state, action: PayloadAction<CompletionId>) {
            state.completion = action.payload;
        },
        setMuted(state, action: PayloadAction<boolean>) {
            state.muted = action.payload;
        },
        setAlarmSound(state, action: PayloadAction<AlarmSoundId>) {
            state.alarmSound = action.payload;
        },
        setLockScreenAlarm(state, action: PayloadAction<boolean>) {
            state.lockScreenAlarm = action.payload;
        },
        setStreakReminders(state, action: PayloadAction<boolean>) {
            state.streakReminders = action.payload;
        },
        setDuration(
            state,
            action: PayloadAction<{ key: keyof Durations; minutes: number }>
        ) {
            const d = state.durations;
            d[action.payload.key] = clampMinutes(action.payload.minutes);
            // The roulette limits never cross.
            if (d.rouletteMin > d.rouletteMax) {
                if (action.payload.key === 'rouletteMin')
                    d.rouletteMax = d.rouletteMin;
                else d.rouletteMin = d.rouletteMax;
            }
        },
        setLifetime(state, action: PayloadAction<boolean>) {
            state.lifetime = action.payload;
        },
        focusCompleted(
            state,
            action: PayloadAction<{ now?: number } | undefined>
        ) {
            const now = new Date(action.payload?.now ?? Date.now());
            const today = dayKey(now);
            const s = state.stats;
            if (s.lastDay === today) {
                s.today += 1;
            } else {
                // Weekends are rest days: only a missed weekday ends the streak.
                if (s.lastDay && missedWeekdays(s.lastDay, today) === 0) {
                    s.streak += 1;
                    s.lostStreak = null;
                } else {
                    s.lostStreak = canRecover(s, today)
                        ? { streak: s.streak, day: today }
                        : null;
                    s.streak = 1;
                }
                s.today = 1;
            }
            s.lastDay = today;
            s.total += 1;
        },
        /** The lost streak was won back: it picks up from where it was, plus today's session. */
        streakRecovered(
            state,
            action: PayloadAction<{ now?: number } | undefined>
        ) {
            const s = state.stats;
            if (!s.lostStreak) return;
            s.streak = s.lostStreak.streak + 1;
            s.lastRecovery = dayKey(
                new Date(action.payload?.now ?? Date.now())
            );
            s.lostStreak = null;
        },
        streakRecoveryDeclined(state) {
            state.stats.lostStreak = null;
        },
    },
});

export const {
    setLook,
    setCompletion,
    setMuted,
    setAlarmSound,
    setLockScreenAlarm,
    setDuration,
    setLifetime,
    setStreakReminders,
    focusCompleted,
    streakRecovered,
    streakRecoveryDeclined,
} = tomatoSlice.actions;
export default tomatoSlice;
